import React, { useState } from 'react';
import { Button } from '@appkit4/react-components/button';
import { FileModel, Upload } from '@appkit4/react-components/upload';
import {
    useCreateImportFromCleansedFile,
    useRefreshPendingCleanseJobs,
    useTriggerFileCleanse,
    useUploadCleanseFile,
} from '../../api/generated/endpoint';
import type { ImportUploadResponse } from '../../api/generated/models';
import {
    CleanseDataType,
    cleansingStatusLabel,
    isCleansed,
    isCleansePending,
    useCleanseFiles,
} from '../../api/dataCleanse';
import { getImportUploadErrorMessage } from '../../api/imports';
import './CleanseFileSource.scss';

interface CleanseFileSourceProps {
    entityId?: number;
    timeId?: number;
    dataType: CleanseDataType;
    maxFileSizeBytes: number;
    onImportSourceReady: (response: ImportUploadResponse) => void;
    onError: (message?: string) => void;
}

const CleanseFileSource: React.FC<CleanseFileSourceProps> = ({
    entityId,
    timeId,
    dataType,
    maxFileSizeBytes,
    onImportSourceReady,
    onError,
}) => {
    const [busyFileId, setBusyFileId] = useState<string>();
    const hasPeriod = Boolean(entityId && timeId);

    const { data: files = [], refetch } = useCleanseFiles(
        { entityId: entityId ?? 0, timeId: timeId ?? 0, dataType },
        hasPeriod
    );

    const uploadCleanseFile = useUploadCleanseFile();
    const triggerCleanse = useTriggerFileCleanse();
    const refreshPending = useRefreshPendingCleanseJobs();
    const createImportSource = useCreateImportFromCleansedFile();

    const params = {
        entityId: entityId ?? 0,
        timeId: timeId ?? 0,
        dataType,
    };

    const run = async (fileId: string, action: () => Promise<void>) => {
        setBusyFileId(fileId);
        onError(undefined);

        try {
            await action();
        } catch (error: unknown) {
            onError(getImportUploadErrorMessage(error));
        } finally {
            setBusyFileId(undefined);
        }
    };

    const handleUpload = (uploadFile: FileModel): void => {
        const file = uploadFile.originFile;

        if (!(file instanceof File) || !entityId || !timeId) {
            onError('Select an entity and time period before uploading.');
            return;
        }

        if (file.size > maxFileSizeBytes) {
            onError('File size must not exceed 100 MB.');
            return;
        }

        void run(file.name, async () => {
            const uploaded = await uploadCleanseFile.mutateAsync({
                data: {
                    EntityId: entityId,
                    TimeId: timeId,
                    DataType: dataType,
                    File: file,
                },
            });

            if (uploaded.fileId) {
                await triggerCleanse.mutateAsync({
                    fileId: uploaded.fileId,
                    params,
                });
            }

            await refetch();
        });
    };

    const handleUse = (fileId: string): void =>
        void run(fileId, async () => {
            const response = await createImportSource.mutateAsync({
                fileId,
                params,
            });
            onImportSourceReady(response);
        });

    const handleRefresh = (): void =>
        void run('', async () => {
            await refreshPending.mutateAsync();
            await refetch();
        });

    return (
        <div className="cleanse-file-source">
            <Upload
                onChange={handleUpload}
                multiple={false}
                autoUpload={false}
                acceptFileType=".XLS,.XLSX"
                uploadTitle="Upload a file to cleanse"
                uploadInstruction="The file is cleansed before it can be mapped. Excel files only, up to 100 MB."
                maxFileSize={maxFileSizeBytes}
                config={{ trigger: false, type: 'inline', size: true }}
            />

            <div className="cleanse-file-source__header">
                <span>Cleansed files from the last 7 days</span>
                <Button
                    kind="text"
                    onClick={handleRefresh}
                    disabled={busyFileId !== undefined}
                >
                    Refresh
                </Button>
            </div>

            {files.length === 0 ? (
                <p className="cleanse-file-source__hint">
                    No files have been sent for cleansing for this period yet.
                </p>
            ) : (
                <ul className="cleanse-file-source__list">
                    {files.map((file) => (
                        <li
                            className="cleanse-file-source__item"
                            key={file.fileId}
                        >
                            <span className="cleanse-file-source__name">
                                {file.fileName}
                            </span>
                            <span
                                className={`cleanse-file-source__status cleanse-file-source__status--${file.cleansingStatus}`}
                            >
                                {cleansingStatusLabel(file)}
                                {isCleansePending(file) && '…'}
                            </span>
                            <Button
                                kind="secondary"
                                disabled={
                                    !isCleansed(file) ||
                                    busyFileId !== undefined
                                }
                                onClick={() => handleUse(file.fileId ?? '')}
                            >
                                {busyFileId === file.fileId
                                    ? 'Loading…'
                                    : 'Use this file'}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CleanseFileSource;
